import { useEffect, useRef, useState } from 'react';
import type { Sample } from '../data/types';
import { useObjectUrl } from './useObjectUrl';
import './ClipChip.css';

export interface ClipChipProps {
  sample: Sample;
  index: number;
  onDelete: (id: string) => void;
  /** Optional click handler — opens the sample modal so the kid can
   *  navigate prev/next, reassign, or delete from there. */
  onOpen?: (id: string) => void;
}

/**
 * Per-clip chip in the audio class grid. Each clip is a 1-second WAV
 * blob; the kid can play it back, hear what was actually captured, and
 * delete it if it's not what they wanted. Mirrors TM's per-extracted-
 * sample affordance.
 */
export function ClipChip({ sample, index, onDelete, onOpen }: ClipChipProps): JSX.Element {
  const url = useObjectUrl(sample.blob);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onEnded = (): void => setPlaying(false);
    el.addEventListener('ended', onEnded);
    el.addEventListener('pause', onEnded);
    return () => {
      el.removeEventListener('ended', onEnded);
      el.removeEventListener('pause', onEnded);
    };
  }, []);

  const togglePlay = (): void => {
    const el = audioRef.current;
    if (!el || !url) return;
    if (playing) {
      el.pause();
      el.currentTime = 0;
      setPlaying(false);
    } else {
      el.currentTime = 0;
      void el
        .play()
        .then(() => setPlaying(true))
        .catch(() => setPlaying(false));
    }
  };

  return (
    <div className={`bl-clip${playing ? ' bl-clip--playing' : ''}`}>
      <button
        type="button"
        className="bl-clip__play"
        onClick={togglePlay}
        disabled={!url}
        aria-label={playing ? `Stop clip ${index + 1}` : `Play clip ${index + 1}`}
      >
        <span aria-hidden="true">{playing ? '⏸' : '▶'}</span>
      </button>
      {onOpen ? (
        <button
          type="button"
          className="bl-clip__open"
          onClick={() => onOpen(sample.id)}
          aria-label={`Open clip ${index + 1}`}
        >
          <span className="bl-clip__label" aria-hidden="true">
            ♪ 1s
          </span>
          <span className="bl-clip__index">#{index + 1}</span>
        </button>
      ) : (
        <>
          <span className="bl-clip__label" aria-hidden="true">
            ♪ 1s
          </span>
          <span className="bl-clip__index">#{index + 1}</span>
        </>
      )}
      <button
        type="button"
        className="bl-clip__delete"
        onClick={() => onDelete(sample.id)}
        aria-label={`Delete clip ${index + 1}`}
      >
        ×
      </button>
      {url && (
        <>
          {/* eslint-disable-next-line jsx-a11y/media-has-caption -- a kid's own recording has no caption track to attach. */}
          <audio ref={audioRef} src={url} preload="metadata" hidden />
        </>
      )}
    </div>
  );
}
